// @flow
import type { Action, State } from './types';
import { PAGE_SIZE } from './actions';
import { isEmpty } from 'lodash';
import {
  generateDvrPages,
  replaceHttpLinkWithHttpsDvrEntries,
  replaceHttpLinkWithHttpsAirings,
  replaceHttpLinkWithHttpsMoviesAndShows,
  filterDvrRecordingsBySearchKeyword,
} from './utils';

export const initialState: State = {
  searchKeyWord: '',
  airings: {
    totalCount: null,
    pagesAmount: null,
    pages: [],
    searchKeyWord: '',
  },
  moviesAndShows: {
    totalCount: null,
    pagesAmount: null,
    pages: [],
    searchKeyWord: '',
  },
  dvrs: {
    totalCount: null,
    pagesAmount: null,
    pages: [],
    searchKeyWord: '',
  },
  autoComplete: {
    completions: [],
    completionToken: 0,
    fetching: false,
    error: false,
  },
  recentSearches: [],
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_AUTOCOMPLETE': {
      /* This branch should preserve previous results of search */
      const { completions } = state.autoComplete;
      const { token } = action;
      return {
        ...state,
        autoComplete: {
          /* Do not reset autocompletions to prevent screen flickering */
          completions,
          completionToken: token,
          fetching: true,
          error: false,
        },
      };
    }
    case 'RECEIVE_AUTOCOMPLETIONS': {
      /* This branch should replace results of previous search  */
      const { completionToken: lastToken } = state.autoComplete;
      const { payload, token } = action;

      return lastToken !== token
        ? state
        : {
            ...state,
            autoComplete: {
              completions: payload.filter(({ type }) => type.toLowerCase() !== 'actors'),
              completionToken: lastToken,
              fetching: false,
              error: false,
            },
          };
    }
    case 'REQUEST_AUTOCOMPLETE_ERROR': {
      /* This branch should preserve previous results of search */
      const { autoComplete } = state;

      return {
        ...state,
        autoComplete: {
          ...autoComplete,
          fetching: false,
          error: true,
        },
      };
    }
    case 'RESET_AUTOCOMPLETE': {
      const { completionToken } = state.autoComplete;
      return {
        ...state,
        autoComplete: {
          /* Token shouldn't be erased here - it may lead to collisions ang bugs */
          completionToken,
          completions: [],
          fetching: false,
          error: false,
        },
      };
    }
    case 'REQUEST_MOVIES_AND_SHOWS': {
      const { moviesAndShows } = state;
      const { pageId } = action;
      const pageFromStore = moviesAndShows.pages[pageId];

      const page = pageFromStore
        ? { ...pageFromStore }
        : { id: pageId, fetching: true, error: false, content: [] };

      const pages = [...moviesAndShows.pages];
      pages[pageId] = page;

      return {
        ...state,
        moviesAndShows: {
          ...moviesAndShows,
          pages,
        },
      };
    }
    case 'RECEIVE_MOVIES_AND_SHOWS': {
      const { totalCount, content, pageId, keyword, searchType } = action.payload;
      const pagesAmount = Math.ceil(totalCount / PAGE_SIZE);

      /* TODO: remove it once radish stop sending http images */
      replaceHttpLinkWithHttpsMoviesAndShows(content);

      const page = {
        ...state.moviesAndShows.pages[pageId],
        fetching: false,
        error: false,
        content,
      };

      const pages = [...state.moviesAndShows.pages];
      pages[pageId] = page;

      return {
        ...state,
        moviesAndShows: { totalCount, pagesAmount, pages, searchKeyWord: keyword, searchType },
      };
    }
    case 'REQUEST_MOVIES_AND_SHOWS_ERROR': {
      const { pageId } = action;
      const { moviesAndShows } = state;

      const page = { ...moviesAndShows.pages[pageId], error: true, fetching: false };
      const pages = [...moviesAndShows.pages];
      pages[pageId] = page;

      return { ...state, moviesAndShows: { ...moviesAndShows, pages } };
    }
    case 'REQUEST_AIRINGS': {
      const { airings } = state;
      const { pageId } = action;
      const pageFromStore = airings.pages[pageId];

      const page = pageFromStore
        ? { ...pageFromStore }
        : { id: pageId, fetching: true, error: false, content: [] };

      const pages = [...airings.pages];
      pages[pageId] = page;

      return { ...state, airings: { ...state.airings, pages } };
    }
    case 'RECEIVE_AIRINGS': {
      const { content, totalCount, pageId, keyword } = action.payload;

      const page = {
        id: pageId,
        fetching: false,
        error: false,
        content,
      };

      /* TODO: remove it once radish stop sending http images */
      replaceHttpLinkWithHttpsAirings(content);

      const pages = [...state.airings.pages];
      pages[pageId] = page;

      const pagesAmount = Math.ceil(totalCount / PAGE_SIZE);

      return { ...state, airings: { totalCount, pagesAmount, pages, searchKeyWord: keyword } };
    }
    case 'REQUEST_AIRINGS_ERROR': {
      const { airings } = state;
      const { pageId } = action;
      const page = {
        id: pageId,
        fetching: false,
        error: true,
        content: [],
      };
      const pages = [...airings.pages];
      pages[pageId] = page;

      return { ...state, airings: { ...airings, pages } };
    }
    case 'REQUEST_DVR_RECORDINGS': {
      const { dvrs } = state;
      const pageId = 0;

      const pageFromStore = dvrs.pages[pageId];
      const page = pageFromStore
        ? { ...pageFromStore }
        : { id: pageId, content: [], fetching: true, error: false };

      const pages = [...dvrs.pages];
      pages[pageId] = page;

      return { ...state, dvrs: { ...dvrs, pages } };
    }
    case 'RECEIVE_DVR_RECORDINGS': {
      /* NOTE: since search for dvr is not available, action uses all available dvrs and splits it to pages in-place */
      const { content, keyword } = action.payload;

      /* Filter dvr recordings by keyword in-place, because currently there is no endpoint for searches over dvrs */
      const recordingsFilteredByKeyword = isEmpty(content)
        ? []
        : filterDvrRecordingsBySearchKeyword(content, keyword);

      const pages = generateDvrPages(recordingsFilteredByKeyword);
      const totalCount = recordingsFilteredByKeyword.length;
      const pagesAmount = pages.length;

      /* TODO: remove it once radish stop sending http images */
      if (!isEmpty(content)) {
        replaceHttpLinkWithHttpsDvrEntries(content);
      }

      return {
        ...state,
        dvrs: {
          totalCount,
          pagesAmount,
          pages,
          searchKeyWord: keyword,
        },
      };
    }
    case 'REQUEST_DVR_RECORDINGS_ERROR': {
      const { dvrs } = state;
      const pageId = 0;
      const page = { id: pageId, content: [], error: true, fetching: false };
      return { ...state, dvrs: { ...dvrs, pages: [page] } };
    }
    case 'RESET_SEARCH_DATA': {
      return { ...initialState };
    }
    case 'SET_SEARCH_KEYWORD': {
      const searchKeyWord = action.payload;
      return { ...state, searchKeyWord };
    }
    case 'LOAD_RECENT_SEARCHES': {
      const recentSearches = action.payload;
      return { ...state, recentSearches };
    }
    case 'ADD_TO_RECENT_SEARCHES': {
      const recentSearches = action.payload;
      return { ...state, recentSearches };
    }
    case 'REMOVE_RECENT_SEARCH': {
      const recentSearches = action.payload;
      return { ...state, recentSearches };
    }
    case 'RESET_SEARCH_KEYWORD': {
      return {
        ...state,
        searchKeyWord: '',
      };
    }
    case 'INVALIDATE_MOVIES_AND_SHOWS_SEARCH_CACHE': {
      return {
        ...state,
        moviesAndShows: {
          totalCount: null,
          pagesAmount: null,
          pages: [],
          searchKeyWord: '',
        },
      };
    }
    case 'INVALIDATE_DVR_SEARCH_CACHE': {
      return {
        ...state,
        dvrs: {
          totalCount: null,
          pagesAmount: null,
          pages: [],
          searchKeyWord: '',
        },
      };
    }
    case 'INVALIDATE_AIRINGS_SEARCH_CACHE': {
      return {
        ...state,
        airings: {
          totalCount: null,
          pagesAmount: null,
          pages: [],
          searchKeyWord: '',
        },
      };
    }
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
